import { useEffect, useState } from 'react'
import { DeviceAvailability } from '../../model/DeviceIncident'
import { deviceIncidentsApi } from './DeviceIncidentsApi'

/**
 * Devices that cannot be ordered at a patient's facility right now.
 *
 * Every ordering surface (appointment device panel, VDG test modal, outpatient modal, symptom bot)
 * shares this hook so the "Currently unavailable" rule has exactly one implementation. Those screens
 * each build their own flattened device list, and duplicating the lookup four times is how the two
 * would drift.
 *
 * Fails open: if the lookup errors we return an empty map, so a transient problem can't make every
 * device look broken and block all ordering. An unavailable device that stays orderable is a wasted
 * dispatch; an available device that can't be ordered blocks patient care.
 */
export function useDeviceAvailability(facilityId?: string): {
  unavailable: DeviceAvailability
  isLoading: boolean
} {
  const [unavailable, setUnavailable] = useState<DeviceAvailability>({})
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    // No facility (patient not synced from PCC, or still loading) => nothing to gate on.
    if (!facilityId) {
      setUnavailable({})
      return
    }

    let cancelled = false
    setIsLoading(true)

    deviceIncidentsApi
      .getAvailability(facilityId)
      .then(response => {
        if (!cancelled) {
          setUnavailable(response?.unavailable ?? {})
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUnavailable({})
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [facilityId])

  return { unavailable, isLoading }
}

/**
 * The label shown on a device that's currently out of service, e.g.
 * "Currently unavailable · back ~Aug 12". `t` is passed in so this stays usable from components that
 * already hold a translation function, without this module taking an i18n dependency.
 */
export function unavailableLabel(
  entry: { expectedReturnAt?: string; notAtFacility?: boolean } | undefined,
  t: (key: string) => string,
): string {
  if (!entry) {
    return ''
  }

  if (entry.notAtFacility) {
    return t('deviceIncidents.notAtFacility')
  }

  const base = t('deviceIncidents.currentlyUnavailable')

  if (!entry.expectedReturnAt) {
    return base
  }

  const back = new Date(entry.expectedReturnAt)
  if (Number.isNaN(back.getTime())) {
    return base
  }

  return `${base} · ${t('deviceIncidents.backAround')} ${back.toLocaleDateString()}`
}
